import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import SiteChrome from './SiteChrome';

const privacyUrl = 'https://bettermedaily.co.za/privacy.html';
const mainTermsUrl = 'https://bettermedaily.co.za/terms.html';
const inputClass = 'w-full rounded-[3px] border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1.5';

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    howHeard: '',
    totalAudience: '',
    agree: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match.');
      }

      // First register the user account
      const name = `${formData.firstName} ${formData.lastName}`;
      try {
        await api.registerAffiliateUser(
          formData.email,
          formData.password,
          name,
          formData.agree,
          formData.phone,
          formData.howHeard,
          Number(formData.totalAudience || 0)
        );
      } catch (registerErr) {
        // If user already exists, try to login instead
        if (registerErr.message.includes('already exists') || registerErr.message.includes('registered')) {
          await api.login(formData.email, formData.password);
        } else {
          throw registerErr;
        }
      }
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteChrome>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-6 sm:py-8">
        <div className="rounded-[3px] border border-gray-100 bg-white shadow-xl shadow-slate-200/60 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-primary-500 via-primary-600 to-slate-900" />
          <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="text-center mb-5">
    
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Join the Better Me Affiliate Program
            </h1>

          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Sign Up Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="John"
                />
              </div>
              <div>
                <label className={labelClass}>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className={labelClass}>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="+27 12 345 6789"
              />
            </div>

            <div>
              <label className={labelClass}>How did you hear about us? *</label>
              <select
                name="howHeard"
                value={formData.howHeard}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">Select an option</option>
                <option value="social">Social Media</option>
                <option value="friend">Friend or Colleague</option>
                <option value="search">Search Engine</option>
                <option value="app">Better Me App</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>Total audience on social media *</label>
              <input
                type="number"
                name="totalAudience"
                value={formData.totalAudience}
                onChange={handleChange}
                required
                min="0"
                className={inputClass}
                placeholder="5000"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className={inputClass}
                  placeholder="Password"
                />
              </div>
              <div>
                <label className={labelClass}>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className={inputClass}
                  placeholder="Confirm password"
                />
              </div>
            </div>

            {/* Benefits Section */}
            <div className="rounded-[3px] bg-gradient-to-br from-primary-50 to-slate-50 border border-primary-100 p-4">
              <h3 className="font-semibold text-sm text-gray-900 mb-3">What You'll Get</h3>
              <div className="grid sm:grid-cols-2 gap-2.5">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span className="text-sm text-gray-700">R1.00 per active user sign-up</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span className="text-sm text-gray-700">50% commission on premium subscriptions</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span className="text-sm text-gray-700">Real-time dashboard to track earnings</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span className="text-sm text-gray-700">Monthly payouts to your South African bank account</span>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="agree"
                checked={formData.agree}
                onChange={handleChange}
                required
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label className="ml-2 text-sm text-gray-600">
                I agree to the <Link to="/terms" className="text-primary-600 hover:underline">Affiliate Terms</Link>, <a href={mainTermsUrl} className="text-primary-600 hover:underline">Main App Terms</a>, and <a href={privacyUrl} className="text-primary-600 hover:underline">Privacy Policy</a>, and I will not make therapy, diagnosis, crisis-support, or regulated financial-advice claims when promoting Better Me. *
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 text-white text-sm py-3 rounded-[3px] font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg shadow-primary-100"
            >
              {loading ? 'Creating Account...' : 'Join Affiliate Program'}
            </button>
          </form>

          {/* Already have account */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:underline font-medium">
                Login here
              </Link>
            </p>
          </div>

          </div>
        </div>
      </div>
    </SiteChrome>
  );
};

export default Signup;
